import type { MouseEvent } from 'react';
import { Link, useNavigate } from 'react-router';
import { Avatar } from '../../components/Avatar';
import { CategoryTag, VerifiedBadge } from '../../components/Badges';
import { Icon } from '../../components/Icon';
import { Paper } from '../../components/Paper';
import type { Post } from '../../lib/api';
import { linkify } from '../../lib/linkify';
import { fullTime, relativeTime } from '../../lib/time';
import { PhotoGrid } from '../photos/PhotoGrid';
import { PostMenu } from './PostMenu';
import { ThanksButton } from './ThanksButton';
import styles from './PostCard.module.css';

interface Props {
  post: Post;
  /** "feed" clamps long notes and opens the thread on click; "thread" shows everything. */
  variant?: 'feed' | 'thread';
  onEdit?: () => void;
}

const LONG_NOTE = 480;

function repliesLabel(post: Post) {
  if (post.neighborCount >= 3) return `${post.neighborCount} neighbors are talking about this`;
  if (post.replyCount === 0) return 'Reply';
  return `${post.replyCount} ${post.replyCount === 1 ? 'reply' : 'replies'}`;
}

export function PostCard({ post, variant = 'feed', onEdit }: Props) {
  const navigate = useNavigate();
  const inFeed = variant === 'feed';
  const threadHref = `/p/${post.id}`;
  const isLong = (post.body?.length ?? 0) > LONG_NOTE || (post.body?.split('\n').length ?? 0) > 9;
  const hidden = post.isDeleted || post.isRemoved;

  // Clicking the card's paper opens the thread, but never steals clicks on
  // links/buttons or interrupts someone selecting text.
  const onCardClick = (event: MouseEvent<HTMLElement>) => {
    if (!inFeed) return;
    if ((event.target as HTMLElement).closest('a, button, [role="menu"], dialog')) return;
    if (window.getSelection()?.toString()) return;
    navigate(threadHref);
  };

  return (
    <Paper
      as="article"
      padded={false}
      className={[styles.card, !inFeed && styles.static, post.category && styles.hasTab].filter(Boolean).join(' ')}
      onClick={onCardClick}
      aria-labelledby={`post-${post.id}-author`}
    >
      {post.category && <CategoryTag category={post.category} className={styles.tab} />}

      <header className={styles.header}>
        <Avatar name={post.author.name} hue={post.author.hue} />
        <div className={styles.who}>
          <div className={styles.nameRow}>
            <Link id={`post-${post.id}-author`} to={`/u/${post.author.id}`} className={styles.name}>
              {post.author.name}
            </Link>
            {post.author.isNeighbor && <VerifiedBadge />}
          </div>
          <span className={styles.meta}>
            <Link to={threadHref}>
              <time dateTime={post.createdAt} title={fullTime(post.createdAt)}>
                {relativeTime(post.createdAt)}
              </time>
            </Link>
            {post.editedAt && ' (edited)'}
          </span>
        </div>
        {!hidden && <PostMenu post={post} onEdit={onEdit} />}
      </header>

      {hidden && !post.body ? (
        <p className={styles.removed}>
          {post.isRemoved ? 'A moderator removed this note.' : 'The author deleted this note.'}
        </p>
      ) : (
        <>
          <p
            className={[styles.body, !inFeed && styles.large, inFeed && isLong && styles.clamped]
              .filter(Boolean)
              .join(' ')}
          >
            {linkify(post.body ?? '')}
          </p>
          {inFeed && isLong && (
            <Link to={threadHref} className={styles.readMore}>
              Read the whole note
            </Link>
          )}
          <PhotoGrid photos={post.photos} authorName={post.author.name} />
          {hidden && <p className={styles.removed}>Only moderators can see this removed note.</p>}
        </>
      )}

      {!hidden && (
        <footer className={styles.footer}>
          {inFeed ? (
            <Link
              to={threadHref}
              className={[styles.replies, post.neighborCount >= 3 && styles.talking].filter(Boolean).join(' ')}
            >
              <Icon name="reply" size={18} />
              {repliesLabel(post)}
            </Link>
          ) : (
            <span className={styles.replies}>
              <Icon name="reply" size={18} />
              {post.replyCount === 0 ? 'No replies yet' : repliesLabel(post)}
            </span>
          )}
          <ThanksButton post={post} />
        </footer>
      )}
    </Paper>
  );
}
